/**
 * Resizable Sidebar Hook
 *
 * Manages sidebar width with drag-to-resize functionality.
 * Persists width to localStorage for user preference.
 * Uses CSS variables and requestAnimationFrame for smooth performance.
 */

"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useCallback, useEffect, useRef } from "react";
const STORAGE_KEY = "sidebar_width";
const CSS_VAR_NAME = "--sidebar-width";
const DEFAULT_WIDTH = 256; // 16rem = 256px (w-64)
const MIN_WIDTH = 200;
const MAX_WIDTH = 480;
type UseResizableSidebarResult = {
  width: number;
  isResizing: boolean;
  handleMouseDown: (e: React.MouseEvent) => void;
  sidebarStyle: React.CSSProperties;
};
export function useResizableSidebar() {
  const $ = _c(13);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const rafRef = useRef(null);
  const currentWidthRef = useRef(DEFAULT_WIDTH);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsedWidth = parseInt(saved, 10);
        if (!isNaN(parsedWidth) && parsedWidth >= MIN_WIDTH && parsedWidth <= MAX_WIDTH) {
          setWidth(parsedWidth);
          currentWidthRef.current = parsedWidth;
          document.documentElement.style.setProperty(CSS_VAR_NAME, `${parsedWidth}px`);
        }
      }
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  const saveWidth = _temp;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = e => {
      e.preventDefault();
      e.stopPropagation();
      startXRef.current = e.clientX;
      startWidthRef.current = currentWidthRef.current;
      setIsResizing(true);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleMouseDown = t2;
  let t3;
  let t4;
  if ($[3] !== isResizing) {
    t3 = () => {
      if (!isResizing) {
        return;
      }
      const handleMouseMove = e_0 => {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
        rafRef.current = requestAnimationFrame(() => {
          const delta = e_0.clientX - startXRef.current;
          const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidthRef.current + delta));
          document.documentElement.style.setProperty(CSS_VAR_NAME, `${newWidth}px`);
          currentWidthRef.current = newWidth;
        });
      };
      const handleMouseUp = () => {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
        setWidth(currentWidthRef.current);
        saveWidth(currentWidthRef.current);
        setIsResizing(false);
      };
      document.addEventListener("mousemove", handleMouseMove, {
        passive: true
      });
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.body.style.pointerEvents = "none";
      const style = document.createElement("style");
      style.textContent = "* { pointer-events: auto !important; }";
      document.head.appendChild(style);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        document.body.style.pointerEvents = "";
        style.remove();
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      };
    };
    t4 = [isResizing, saveWidth];
    $[3] = isResizing;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  const t5 = isResizing ? `var(${CSS_VAR_NAME}, ${width}px)` : width;
  const t6 = isResizing ? "width" : "auto";
  let t7;
  if ($[6] !== t5 || $[7] !== t6) {
    t7 = {
      width: t5,
      willChange: t6,
      contain: "layout"
    };
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const sidebarStyle = t7;
  let t8;
  if ($[9] !== isResizing || $[10] !== sidebarStyle || $[11] !== width) {
    t8 = {
      width,
      isResizing,
      handleMouseDown,
      sidebarStyle
    };
    $[9] = isResizing;
    $[10] = sidebarStyle;
    $[11] = width;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
}
function _temp(w) {
  if (typeof window === "undefined") {
    return;
  }
  localStorage.setItem(STORAGE_KEY, String(w));
}